import * as React from "react"
import { useNavigate } from "react-router-dom"
import { ChevronDownIcon, DownloadIcon, FileDownIcon, PrinterIcon, RefreshCwIcon } from "lucide-react"

import { ReportExportType } from "@ibada/shared-types/reporting"

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  DetailPageHeader,
  MobileScreenHeader,
  Separator,
  cn,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { FinanceReportsSurfaceKind, useFinanceReportsSurface } from "@/features/reports/model/finance-reports-surface"
import { useMobileBack } from "@/features/dashboard"

import { ReportBadge, resolveReportBadge, type FinanceReportEntry } from "@/features/reports/model/report-registry-types"
import type { ReportExporter } from "@/features/reports/services/use-report-export"
import { ReportLockedNotice } from "@/features/reports/components/shell/report-locked-notice"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileHeaderIconButton } from "@/shared/components/mobile-header-icon-button"
import { RowActionsMenu, type RowActionsMenuItem } from "@/shared/components/row-actions-menu"

const EXPORT_FORMAT_LABEL: Record<ReportExportType, string> = {
  [ReportExportType.PDF]: "finance.reports.toolbar.formats.pdf",
  [ReportExportType.EXCEL]: "finance.reports.toolbar.formats.excel",
  [ReportExportType.CSV]: "finance.reports.toolbar.formats.csv",
}

export interface ReportShellProps {
  entry: FinanceReportEntry
  isLoading: boolean
  isError: boolean
  /**
   * The report cannot be run, and no retry will change that (ADR-0024).
   *
   * Distinct from `isError`, which means something went wrong. A refusal is a STATE: a branch-dimension
   * report under branch autonomy would state figures the reader may not see, so the server declines it. It
   * arrived as a red card with a Retry that could never succeed.
   */
  lockedReasonKey?: string | null
  isEmpty?: boolean
  onRefresh: () => void
  /** Export driver; when absent or offering no formats the Export control is disabled (Print still works). */
  exporter?: ReportExporter
  /** Optional right-of-toolbar extras (e.g. an account picker for the ledger). */
  toolbarExtras?: React.ReactNode
  /** Phone: the page's own actions for the ⋯ sheet (configure, save), in place of `toolbarExtras`. */
  mobileMenuItems?: RowActionsMenuItem[]
  /** Phone: a control under the title (e.g. the journal search), in place of `toolbarExtras`. */
  mobileToolbar?: React.ReactNode
  /** Chips summarizing the applied config. */
  appliedFilters?: React.ReactNode
  /** Replaces the generic entry description in the header — e.g. the specific account a ledger is scoped to. */
  descriptionOverride?: React.ReactNode
  children: React.ReactNode
}

const BADGE_LABEL: Record<ReportBadge, string> = {
  [ReportBadge.New]: "finance.reports.badges.new",
  [ReportBadge.Beta]: "finance.reports.badges.beta",
  [ReportBadge.ComingSoon]: "finance.reports.badges.comingSoon",
  [ReportBadge.Parish]: "finance.reports.badges.parish",
}

/**
 * A badge only works if it is SEEN, and only helps if its colour says something.
 *
 * `New` takes the accent because it is an invitation to look; `Beta` and `Coming soon` stay quiet, because
 * they are caveats rather than news. `Parish` follows the catalog card: a scope label has to be seen, and
 * must not read as a verdict on the numbers.
 */
const BADGE_VARIANT: Record<ReportBadge, "success" | "secondary" | "outline" | "info"> = {
  [ReportBadge.New]: "success",
  [ReportBadge.Beta]: "secondary",
  [ReportBadge.ComingSoon]: "outline",
  [ReportBadge.Parish]: "info",
}

/**
 * The one shell every report shares: back-to-catalog header, toolbar (Refresh / Export / Print), applied
 * filters, then the report body (summary + table/chart or a custom renderer). The `print-report-root` class
 * scopes the print stylesheet so Print shows only the report.
 */
export function ReportShell({
  entry,
  isLoading,
  isError,
  lockedReasonKey,
  isEmpty,
  onRefresh,
  exporter,
  toolbarExtras,
  mobileMenuItems = [],
  mobileToolbar,
  appliedFilters,
  descriptionOverride,
  children,
}: ReportShellProps) {
  const isMobile = useIsMobile()
  // What it is wearing TODAY — `newUntil` expires the New badge without anyone tidying up.
  const badge = resolveReportBadge(entry)
  const t = useTranslations()
  const navigate = useNavigate()
  // Back goes to THIS surface's catalog: from a consolidated report, back must not drop the user into the
  // branch catalog (a different scope showing different reports).
  const { reportHref, kind: surfaceKind } = useFinanceReportsSurface()

  const title = entry.titleKey ? t(entry.titleKey as never) : entry.id
  const description = descriptionOverride ?? (entry.descriptionKey ? t(entry.descriptionKey as never) : undefined)
  const exportFormats = exporter?.formats ?? []
  // A refusal, not a failure: the server declines this report and no retry will change that (ADR-0024).
  const isLocked = Boolean(lockedReasonKey)
  const offersPdf = exportFormats.includes(ReportExportType.PDF)
  /* PDF has its own button, so the Export control offers the data formats — the ones you open in a spreadsheet. */
  const dataFormats = exportFormats.filter((format) => format !== ReportExportType.PDF)
  const exportUnavailable = dataFormats.length === 0

  const goToCatalog = () => navigate(reportHref(""))
  // Phone back: history first (the shared rule), else this report's catalog. The consolidated surface has its own
  // catalog outside the branch admin, so it keeps going straight there.
  const backToBranchCatalog = useMobileBack({ fallback: "/reports" })
  const goBack = surfaceKind === FinanceReportsSurfaceKind.Global ? goToCatalog : backToBranchCatalog

  // The report's body: shared by the phone and desktop layouts.
  const body = isLocked ? (
    // Checked BEFORE `isError`: the refusal arrives as a failed request, and showing it as a failure is
    // exactly the bug. Nothing went wrong, and nothing the reader does will change it.
    <ReportLockedNotice reasonKey={lockedReasonKey} />
  ) : isError ? (
    <EmptyState
      tone="danger"
      title={t("finance.reports.error.title" as never)}
      action={
        <Button size="sm" variant="outline" onClick={onRefresh}>
          {t("finance.reports.toolbar.refresh" as never)}
        </Button>
      }
    />
  ) : isEmpty ? (
    <EmptyState title={t("finance.reports.empty.title" as never)} />
  ) : (
    children
  )

  if (isMobile) {
    // Phone: the shared pinned header (back, the full title, the PDF as an icon, ⋯ with the rest). Six labelled
    // buttons beside the title do not fit a phone's width.
    const menuItems: RowActionsMenuItem[] = [
      { id: "refresh", label: t("finance.reports.toolbar.refresh" as never), icon: RefreshCwIcon, onSelect: onRefresh, disabled: isLoading || isLocked },
      ...(exporter
        ? dataFormats.map((format) => ({
            id: `export-${format}`,
            label: t(EXPORT_FORMAT_LABEL[format] as never),
            icon: DownloadIcon,
            onSelect: () => exporter.exportAs(format),
            disabled: exporter.isExporting || isLocked,
          }))
        : []),
    ]
    return (
      <div className="print-report-root flex flex-col gap-3.5" data-testid="report-shell">
        <MobileScreenHeader
          className="print:hidden"
          title={title}
          subtitle={
            badge || description ? (
              <span className="flex min-w-0 items-center gap-2">
                {badge ? (
                  <span className="shrink-0">
                    <Badge variant={BADGE_VARIANT[badge]}>{t(BADGE_LABEL[badge] as never)}</Badge>
                  </span>
                ) : null}
                {description ? <span className="min-w-0">{description}</span> : null}
              </span>
            ) : undefined
          }
          back={{ label: t("finance.reports.backToCatalog" as never), onBack: goBack }}
          actions={
            <>
              {offersPdf && exporter ? (
                <MobileHeaderIconButton
                  icon={FileDownIcon}
                  label={t("finance.reports.toolbar.downloadPdf" as never)}
                  loading={exporter.isExporting}
                  disabled={isLocked}
                  onClick={() => exporter.exportAs(ReportExportType.PDF)}
                />
              ) : (
                <MobileHeaderIconButton
                  icon={PrinterIcon}
                  label={t("finance.reports.toolbar.print" as never)}
                  disabled={isLocked}
                  onClick={() => window.print()}
                />
              )}
              <RowActionsMenu
                label={title}
                openLabel={t("common.dataTableMobile.moreActions" as never)}
                sheetHeader={<span className="text-[15px] font-extrabold text-foreground">{title}</span>}
                groups={[mobileMenuItems, menuItems]}
              />
            </>
          }
        >
          {mobileToolbar}
        </MobileScreenHeader>
        {appliedFilters ? <div className="flex flex-wrap gap-1.5">{appliedFilters}</div> : null}
        {body}
      </div>
    )
  }

  return (
    <div className="print-report-root space-y-4" data-testid="report-shell">
      <DetailPageHeader
        title={title}
        description={description}
        onBack={goToCatalog}
        backLabel={t("finance.reports.backToCatalog" as never)}
        actions={
          <>
            {badge ? <Badge variant={BADGE_VARIANT[badge]}>{t(BADGE_LABEL[badge] as never)}</Badge> : null}
            <div className="flex flex-wrap items-center gap-2 print:hidden">
              {toolbarExtras}
              {/*
                * Disabled while locked. Refresh, Export and Print all act on data the server will not
                * return — leaving them live invites the reader to try, fail, and try again, which is the
                * same "keep hitting the wall" the Retry button did.
                */}
              <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading || isLocked}>
                <RefreshCwIcon className={cn("size-4", isLoading && "animate-spin")} />
                {t("finance.reports.toolbar.refresh" as never)}
              </Button>
              {dataFormats.length > 1 && exporter ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={exporter.isExporting || isLocked}>
                      <DownloadIcon className="size-4" />
                      {t("finance.reports.toolbar.export" as never)}
                      <ChevronDownIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {dataFormats.map((format) => (
                      <DropdownMenuItem key={format} onSelect={() => exporter.exportAs(format)}>
                        {t(EXPORT_FORMAT_LABEL[format] as never)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exporter?.exportAs(dataFormats[0])}
                  disabled={exportUnavailable || !exporter || exporter.isExporting || isLocked}
                  title={exportUnavailable ? t("finance.reports.toolbar.exportUnavailable" as never) : undefined}
                >
                  <DownloadIcon className="size-4" />
                  {/*
                    * Named for what it gives you. Beside a "Download PDF" button, a second button labelled
                    * only "Export" is a guess — and the two were indistinguishable until you opened the file.
                    */}
                  {exportUnavailable || !dataFormats[0]
                    ? t("finance.reports.toolbar.export" as never)
                    : t(EXPORT_FORMAT_LABEL[dataFormats[0]] as never)}
                </Button>
              )}
              {/*
                * Print used to sit here as the only way to get a report onto paper, and `window.print()`
                * produces a screenshot of a web page — toolbar chrome, screen colours, whatever the browser
                * decides to do with a wide table. Every report now offers a real PDF through the shared
                * export framework, so this is the download, not a fallback.
                */}
              {offersPdf && exporter ? (
                <Button
                  size="sm"
                  onClick={() => exporter.exportAs(ReportExportType.PDF)}
                  disabled={exporter.isExporting || isLocked}
                >
                  <FileDownIcon className="size-4" />
                  {t("finance.reports.toolbar.downloadPdf" as never)}
                </Button>
              ) : (
                // Statements draw their own layout, so there is no table to hand the renderer. Until they
                // have a document of their own, the browser's print dialog is all they have.
                <Button variant="outline" size="sm" onClick={() => window.print()} disabled={isLocked}>
                  <PrinterIcon className="size-4" />
                  {t("finance.reports.toolbar.print" as never)}
                </Button>
              )}
            </div>
          </>
        }
      />

      {appliedFilters ? <div className="flex flex-wrap gap-1.5">{appliedFilters}</div> : null}
      <Separator />

      {body}
    </div>
  )
}
